import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { mockData } from '../data/mock';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[rgba(0,0,0,0.1)] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <a
            href="/"
            className="flex items-center gap-2 text-[rgb(0,55,32)] hover:text-[rgb(13,121,22)] transition-colors font-medium"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-4xl font-bold text-[rgb(0,55,32)] mb-2" data-testid="terms-title">Terms of Service</h1>
        <p className="text-[rgb(131,146,140)] mb-8">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none space-y-8 text-[rgb(14,15,12)]">
          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing and using the {mockData.company.name} website, you agree to be bound by these
              Terms of Service. If you do not agree with any part of these terms, please do not use our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">2. Services Description</h2>
            <p className="leading-relaxed">
              {mockData.company.name} provides home renovation services, Secondary Dwelling Unit (SDU)
              creation, and related construction services in Ottawa and surrounding areas. Our website
              provides information about our services, allows you to request consultations, download
              educational resources, and contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">3. Use of Website</h2>
            <p className="leading-relaxed mb-3">When using our website, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate and complete information when submitting forms</li>
              <li>Use the website for lawful purposes only</li>
              <li>Not attempt to disrupt or interfere with the website's functionality</li>
              <li>Not reproduce, distribute, or modify website content without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">4. Consultation Requests</h2>
            <p className="leading-relaxed">
              Submitting a consultation request through our website does not constitute a binding
              contract for services. It serves as an initial inquiry. Any renovation or construction
              agreement will be formalized through a separate written contract between you and {mockData.company.name}.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">5. Cost Estimates</h2>
            <p className="leading-relaxed">
              The cost estimator tool on our website provides approximate ranges for informational
              purposes only. Actual project costs may vary based on specific requirements, site
              conditions, material choices, and other factors. A detailed, accurate quote will be
              provided after an in-person consultation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">6. E-Book and Educational Content</h2>
            <p className="leading-relaxed">
              The renovation guide and educational materials provided through our website are for
              informational purposes only. They do not constitute professional advice. Always consult
              with qualified professionals for specific renovation decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">7. Intellectual Property</h2>
            <p className="leading-relaxed">
              All content on this website, including text, images, logos, project photos, and design
              elements, is the property of {mockData.company.name} and is protected by copyright and
              intellectual property laws. You may not use, reproduce, or distribute our content without
              prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">8. Testimonials and Reviews</h2>
            <p className="leading-relaxed">
              Testimonials and reviews displayed on our website reflect the personal experiences of
              individual clients. Results may vary. We do not guarantee that every client will achieve
              the same results.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">9. Limitation of Liability</h2>
            <p className="leading-relaxed">
              {mockData.company.name} shall not be liable for any indirect, incidental, special, or
              consequential damages arising from the use of our website or reliance on information
              provided herein. Our website is provided "as is" without warranties of any kind.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">10. Third-Party Links</h2>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites for your convenience. We do not
              endorse or assume responsibility for the content, privacy policies, or practices of
              third-party websites.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">11. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms of Service are governed by and construed in accordance with the laws of
              the Province of Ontario, Canada. Any disputes arising from these terms shall be resolved
              in the courts of Ontario.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">12. Changes to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. Changes will be
              effective immediately upon posting to the website. Your continued use of the website
              constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[rgb(0,55,32)] mb-3">13. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-3 p-4 bg-[rgba(45,80,22,0.05)] rounded-xl">
              <p className="font-semibold text-[rgb(0,55,32)]">{mockData.company.name}</p>
              <p>Phone: {mockData.company.phone}</p>
              <p>Email: {mockData.company.email}</p>
              <p>Service Area: Ottawa & Surrounding Areas</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[rgb(0,55,32)] text-white py-8 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center text-sm text-white/60">
          <p>&copy; 2026 {mockData.company.name}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
